import React, { useState } from 'react';
import { Building2, Calculator, DollarSign, Calendar, Percent } from 'lucide-react';

interface BankRate {
  bank: string;
  amount: string;
  term: string;
  rate: string;
}

const bankRates: BankRate[] = [
  { bank: 'Akbank', amount: '1.000 - 10.000.000 TL', term: '3 - 36 Ay', rate: '%4,19 - %4,79' },
  { bank: 'Albaraka', amount: '250 - 50.000 TL', term: '3 - 36 Ay', rate: '%0 - %3,50' },
  { bank: 'Alternatif Bank', amount: '5.000 - 350.000 TL', term: '3 - 36 Ay', rate: '%3,09 - %4,09' },
  { bank: 'CEPTETEB', amount: '1.000 - 400.000 TL', term: '3 - 36 Ay', rate: '%3,39 - %3,39' },
  { bank: 'DenizBank', amount: '1.000 - 500.000 TL', term: '3 - 36 Ay', rate: '%2,99 - %3,69' },
  { bank: 'Enpara', amount: '1.000 - 500.000 TL', term: '3 - 36 Ay', rate: '%3,64 - %4,14' },
  { bank: 'Garanti BBVA', amount: '1.000 - 1.000.000 TL', term: '3 - 36 Ay', rate: '%3,79 - %5,19' },
  { bank: 'GetirFinans', amount: '1.000 - 450.000 TL', term: '3 - 36 Ay', rate: '%3,65 - %3,65' },
  { bank: 'HSBC', amount: '2.000 - 250.000 TL', term: '3 - 36 Ay', rate: '%3,52 - %3,65' },
  { bank: 'Halkbank', amount: '1.000 - 200.000 TL', term: '3 - 36 Ay', rate: '%5,06 - %5,06' },
  { bank: 'ICBC', amount: '1.000 - 100.000 TL', term: '3 - 36 Ay', rate: '%4,58 - %4,58' },
  { bank: 'ING', amount: '1.000 - 350.000 TL', term: '3 - 36 Ay', rate: '%0,99 - %3,79' },
  { bank: 'Kolay', amount: '1.000 - 300.000 TL', term: '3 - 36 Ay', rate: '%3,79 - %3,79' },
  { bank: 'ON', amount: '10.000 - 400.000 TL', term: '3 - 36 Ay', rate: '%3,39 - %3,59' },
  { bank: 'QNB', amount: '1.000 - 400.000 TL', term: '3 - 36 Ay', rate: '%0 - %3,84' },
  { bank: 'TEB', amount: '1.000 - 300.000 TL', term: '3 - 36 Ay', rate: '%3,39 - %3,39' },
  { bank: 'Türkiye İş Bankası', amount: '3.000 - 1.000.000 TL', term: '3 - 36 Ay', rate: '%4,20 - %4,75' },
  { bank: 'Ziraat Bankası', amount: '1.000 - 1.000.000 TL', term: '3 - 36 Ay', rate: '%4,99 - %4,99' },
  { bank: 'Şekerbank', amount: '1.000 - 500.000 TL', term: '3 - 36 Ay', rate: '%5,33 - %5,33' }
];

type LoanType = 'ihtiyac' | 'konut' | 'tasit';

const BankInterestRates = () => {
  const [selectedType, setSelectedType] = useState<LoanType>('ihtiyac');

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-slate-800 to-slate-900 text-white">
        <div className="flex items-center gap-3 mb-3">
          <Calculator className="h-6 w-6 text-[#F7BA45]" />
          <h2 className="text-xl font-bold">Bankaların Faiz Oranları</h2>
        </div>
        <p className="text-slate-300 text-sm">
          Güncel kredi faiz oranlarını karşılaştırın, size en uygun krediyi seçin
        </p>
      </div>

      {/* Loan Type Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex">
          <button
            onClick={() => setSelectedType('ihtiyac')}
            className={`px-6 py-3 text-sm font-medium transition-colors relative
              ${selectedType === 'ihtiyac' 
                ? 'text-[#F7BA45] border-b-2 border-[#F7BA45]' 
                : 'text-slate-600 hover:text-slate-900'}`}
          >
            İhtiyaç Kredisi
          </button>
          <button
            onClick={() => setSelectedType('konut')}
            className={`px-6 py-3 text-sm font-medium transition-colors relative
              ${selectedType === 'konut' 
                ? 'text-[#F7BA45] border-b-2 border-[#F7BA45]' 
                : 'text-slate-600 hover:text-slate-900'}`}
          >
            Konut Kredisi
          </button>
          <button
            onClick={() => setSelectedType('tasit')}
            className={`px-6 py-3 text-sm font-medium transition-colors relative
              ${selectedType === 'tasit' 
                ? 'text-[#F7BA45] border-b-2 border-[#F7BA45]' 
                : 'text-slate-600 hover:text-slate-900'}`}
          >
            Taşıt Kredisi
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50">
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-[#F7BA45]" />
                  Bankalar
                </div>
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-[#F7BA45]" />
                  Kredi Tutarı
                  <span className="text-xs text-slate-500">(min-maks)</span>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-[#F7BA45]" />
                  Kredi Vadesi
                  <span className="text-xs text-slate-500">(min-maks)</span>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                <div className="flex items-center gap-2">
                  <Percent className="h-4 w-4 text-[#F7BA45]" />
                  Faiz/Kâr Payı
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {bankRates.map((bank, index) => (
              <tr 
                key={index}
                className="hover:bg-slate-50 transition-colors"
              >
                <td className="px-6 py-4 text-sm font-medium text-slate-900">{bank.bank}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{bank.amount}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{bank.term}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{bank.rate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Disclaimer */}
      <div className="p-4 bg-slate-50 border-t border-slate-200">
        <p className="text-xs text-slate-500 text-center">
          * Faiz oranları bankaların resmi web sitelerinden alınmıştır ve bilgilendirme amaçlıdır. 
          Kredinin toplam maliyeti; kredi tutarı, vade ve bankanın sunduğu özel faiz oranına göre değişiklik gösterebilir.
        </p>
      </div>
    </div>
  );
};

export default BankInterestRates;